import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { del, errorMessage, get, put } from "../api";
import { Alert, Button, Loading, Panel, PanelHeader, formatDate } from "./ui";

type Label = { id: string; name: string };
type GmailState = { available: boolean; connected: boolean; email: string | null; error: string | null; labels: Label[]; since: string | null };

const RESULT: Record<string, string> = {
  connected: "Gmail is connected. Pick the labels below.",
  cancelled: "Gmail wasn't connected.",
  refused: "Google didn't give Gmail access. Try again and allow it.",
  "wrong-account": "That was a different Google account. Connect with your NMT email.",
  expired: "That took too long. Try again.",
};

/** My profile → Email to Fred: Gmail labels whose email goes to Fred (instead of Zapier). */
export function GmailCard() {
  const qc = useQueryClient();
  const state = useQuery({ queryKey: ["gmail"], queryFn: () => get<GmailState>("/api/gmail") });
  const s = state.data;
  const labels = useQuery({ queryKey: ["gmail-labels"], queryFn: () => get<Label[]>("/api/gmail/labels"), enabled: !!s?.connected });
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    const r = new URLSearchParams(window.location.search).get("gmail");
    if (r) setNotice(RESULT[r] ?? "Gmail wasn't connected.");
  }, []);
  useEffect(() => {
    if (s && picked === null) setPicked(new Set(s.labels.map((l) => l.id)));
  }, [s, picked]);
  if (state.isLoading) return <Loading />;
  if (!s?.available) return null;
  const changed = !!picked && (picked.size !== s.labels.length || s.labels.some((l) => !picked.has(l.id)));
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await put("/api/gmail/labels", { labels: (labels.data ?? []).filter((l) => picked!.has(l.id)) });
      await qc.invalidateQueries({ queryKey: ["gmail"] });
      setNotice("Saved. Email with those labels goes to Fred within a few minutes.");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel>
      <PanelHeader
        title="Email to Fred"
        description="Pick Gmail labels: new email with them goes to Fred, who files receipts and bills to the review queue on the right job (and the rest where it belongs), like email sent to fred@. Fred puts the label CRM/Filed on each one he takes. Use it in place of the Zapier zaps, one label at a time."
      />
      <div className="flex flex-col gap-3 px-5 py-4 text-sm">
        {notice && <Alert tone="info">{notice}</Alert>}
        {(error || s.error) && <Alert>{error ?? s.error}</Alert>}
        {!s.connected ? (
          <div>
            <a href="/api/auth/google/gmail?returnTo=/profile" className="inline-flex rounded-[var(--radius-control)] bg-brand px-4 py-2 font-semibold text-white hover:bg-brand-deep">
              Connect Gmail
            </a>
            <p className="mt-2 text-xs text-muted">The CRM can read your email and add a label; it never sends or deletes anything.</p>
          </div>
        ) : (
          <>
            <div className="text-muted">
              Connected: {s.email}
              {s.since ? ` · email labeled since ${formatDate(s.since)}` : ""}
            </div>
            {labels.isLoading ? (
              <Loading />
            ) : labels.error ? (
              <Alert>{errorMessage(labels.error)}</Alert>
            ) : !labels.data?.length ? (
              <p className="text-muted">No labels of your own in Gmail yet.</p>
            ) : (
              <div className="grid gap-1 sm:grid-cols-2">
                {labels.data.map((l) => (
                  <label key={l.id} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-mist">
                    <input type="checkbox" className="size-4 accent-[var(--color-brand)]" checked={picked?.has(l.id) ?? false} onChange={(e) => setPicked((p) => { const n = new Set(p); if (e.target.checked) n.add(l.id); else n.delete(l.id); return n; })} />
                    {l.name}
                  </label>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" busy={busy} disabled={!changed} onClick={() => void save()}>
                Save labels
              </Button>
              <Button variant="quiet" onClick={async () => (await del("/api/gmail").catch(() => undefined), setPicked(null), void qc.invalidateQueries({ queryKey: ["gmail"] }))}>
                Disconnect
              </Button>
            </div>
            <p className="text-xs text-muted">Only email labeled from the day you first pick a label is taken (not older email). Turn off the matching zap when you turn on its label here, so nothing goes to QuickBooks twice.</p>
          </>
        )}
      </div>
    </Panel>
  );
}
